import { z } from 'zod/v4';
import { HistoryEntrySchema } from './history-entry.js';

/**
 * 20, not the 10 the other CAPA lists default to — both dashboards page the
 * activity feed 20 rows at a time and this is the value they have always got.
 */
export const LIST_HISTORIES_DEFAULT_PAGE_SIZE = 20;

/**
 * The largest page the history lists will serve. A bigger `count` is
 * **clamped**, not rejected, matching the `if (count > 100) count = 100` the
 * handlers did.
 */
export const LIST_HISTORIES_MAX_PAGE_SIZE = 100;

/**
 * The history list query.
 *
 * Only `count` and `page`. The migration plan sketched `sort` and `filter`, but
 * neither route has ever declared them and no caller sends them — ordering is
 * fixed to `created_at DESC`. (The *legacy* v1 twin does accept a `createdAt`
 * sort and a `userId` filter; it stays where it is for the old dashboard's v1
 * views.) Adding them here would widen the surface with no consumer.
 */
export const ListHistoriesQuerySchema = z.object({
  count: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(1).optional(),
    )
    .default(LIST_HISTORIES_DEFAULT_PAGE_SIZE)
    .transform(value => Math.min(value, LIST_HISTORIES_MAX_PAGE_SIZE)),
  page: z
    .preprocess(
      val => val ?? undefined,
      z.coerce.number().int().min(0).optional(),
    )
    .default(0),
});

export const ListHistoriesResponseSchema = z.object({
  data: z.array(HistoryEntrySchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

/** `GET /api/capa/actions/:actionId/histories` path params. */
export const ActionHistoriesParamsSchema = z.object({
  actionId: z.string().min(1).max(36),
});

/** `GET /api/capa/caps/:capId/histories` path params. */
export const PlanHistoriesParamsSchema = z.object({
  capId: z.string().min(1).max(36),
});

export type ListHistoriesQuery = z.infer<typeof ListHistoriesQuerySchema>;
export type ListHistoriesResponse = z.infer<typeof ListHistoriesResponseSchema>;
export type ActionHistoriesParams = z.infer<typeof ActionHistoriesParamsSchema>;
export type PlanHistoriesParams = z.infer<typeof PlanHistoriesParamsSchema>;
